"use client";

import { useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { es } from "date-fns/locale";
import { Toaster } from "sonner";

import { theme } from "@/theme/mui-theme";

// Registrar licencia MUI X. Una sola vez al cargar el bundle.
const MUI_X_LICENSE = process.env.NEXT_PUBLIC_MUI_X_LICENSE_KEY;
if (MUI_X_LICENSE) {
  LicenseInfo.setLicenseKey(MUI_X_LICENSE);
}

function buildQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // `staleTime: 0` → cada vez que un componente se monta o se
        // invalida una query, React Query hace fetch al backend. Es la
        // unica forma de garantizar que tras "Guardar" siempre veas los
        // datos persistidos, sin que el cache sirva versiones antiguas.
        staleTime: 0,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        // Refetcheamos al re-montar para que volver con el boton "Atras"
        // del navegador traiga datos frescos en vez de cache stale.
        refetchOnMount: "always",
      },
    },
  });
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(buildQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
          <ReactQueryDevtools initialIsOpen={false} />
          <Toaster richColors position="top-center" />
        </ThemeProvider>
      </LocalizationProvider>
    </QueryClientProvider>
  );
}
